import * as THREE from "three";
import { createRng } from "@shared/game";

interface Sample {
  s: number; // arc length from start
  pos: THREE.Vector3;
  tan: THREE.Vector3; // unit tangent in XZ plane
}

interface PlatformStep {
  start: number;
  rampUpEnd: number;
  topEnd: number;
  dropEnd: number;
  height: number;
}

interface NarrowBridgeSection {
  center: number;
  start: number;
  end: number;
  halfWidth: number;
}

/**
 * Curved running path. Built from a CatmullRom spline through random
 * waypoints (seeded). Provides:
 *   - positionAt(s, laneOffset) — world position at arc length `s`, offset
 *     perpendicular to the tangent by `laneOffset` (lane swing).
 *   - tangentAt(s) — unit tangent direction at `s`.
 *   - yawAt(s) — Y-axis rotation matching the tangent (for orienting meshes).
 *
 * Players, obstacles, coins and gates all live along this curve via their
 * `s` value; the world bends around them and the camera follows naturally.
 */
export class Path {
  private samples: Sample[] = [];
  private platforms: PlatformStep[] = [];
  private narrowBridgeSectionsCache: NarrowBridgeSection[] | null = null;
  public length: number;
  /** Approximate arc-length positions of each corner — used by Spawner to avoid
   *  placing obstacles right on a turn (can't see them around the bend). */
  public cornerS: number[] = [];

  /** True if `s` is within `radius` of any corner. */
  isNearCorner(s: number, radius = 8): boolean {
    for (const cs of this.cornerS) if (Math.abs(cs - s) < radius) return true;
    return false;
  }

  narrowBridgeSections(): NarrowBridgeSection[] {
    if (this.narrowBridgeSectionsCache) return this.narrowBridgeSectionsCache;
    const length = 34;
    const halfWidth = 2.35;
    const candidates = [0.54, 0.46, 0.64].map((t) => this.length * t);
    let center = candidates[0]!;
    for (const c of candidates) {
      if (!this.isNearCorner(c, 32) && !this.isNearCorner(c - length / 2, 18) && !this.isNearCorner(c + length / 2, 18)) {
        center = c;
        break;
      }
    }
    this.narrowBridgeSectionsCache = [{
      center,
      start: Math.max(42, center - length / 2),
      end: Math.min(this.length - 52, center + length / 2),
      halfWidth,
    }];
    return this.narrowBridgeSectionsCache;
  }

  narrowBridgeHalfWidthAt(s: number): number | null {
    const section = this.narrowBridgeSections().find((b) => s >= b.start && s <= b.end);
    return section ? section.halfWidth : null;
  }

  constructor(targetLength: number, seed: number) {
    const rng = createRng(seed);
    const points: THREE.Vector3[] = [];
    // Pre-roll for a clean tangent at s=0.
    points.push(new THREE.Vector3(0, 0, -20));
    points.push(new THREE.Vector3(0, 0, -10));

    // Walk forward by tracking a heading angle (0 = +Z). Periodically apply
    // a sharp turn so the course actually has corners (Temple Run-style)
    // instead of just gentle sideways drift.
    const pos = new THREE.Vector3(0, 0, 0);
    let heading = 0;
    let s = 0;

    // Opening straight — give the player ~30m to orient before the first turn.
    while (s < 30) {
      pos.z += 8;
      s += 8;
      points.push(pos.clone());
    }

    // Cornered section. ~30% of waypoints kick a real corner (35-80°),
    // the rest are straight steps. Two corners can't appear back-to-back
    // (gives the player a beat to recover and lets obstacle spawning leave
    // a clean buffer around each turn).
    let lastTurnSign = 0;
    let stepsSinceCorner = 99;
    this.cornerS = [];
    while (s < targetLength - 30) {
      const wantCorner = stepsSinceCorner >= 2 && rng() < 0.3;
      if (wantCorner) {
        let sign = rng() < 0.5 ? -1 : 1;
        // Avoid two same-direction corners in close succession (looks like a loop).
        if (stepsSinceCorner < 4 && sign === lastTurnSign) sign = -sign;
        const turnDeg = 35 + rng() * 45;
        heading += ((turnDeg * Math.PI) / 180) * sign;
        lastTurnSign = sign;
        stepsSinceCorner = 0;
        // Mark the corner's arc position so Spawner can avoid it.
        this.cornerS.push(s);
        // Tighter post-corner point so the curve hugs the bend instead of
        // straight-lining diagonally between waypoints.
        const tight = 5;
        pos.x += Math.sin(heading) * tight;
        pos.z += Math.cos(heading) * tight;
        s += tight;
        points.push(pos.clone());
      }
      const stepDist = 18 + rng() * 10;
      pos.x += Math.sin(heading) * stepDist;
      pos.z += Math.cos(heading) * stepDist;
      s += stepDist;
      points.push(pos.clone());
      stepsSinceCorner++;
    }

    // Final straight runway so the finish line is dead ahead.
    for (let i = 0; i < 2; i++) {
      pos.x += Math.sin(heading) * 14;
      pos.z += Math.cos(heading) * 14;
      points.push(pos.clone());
    }

    // `centripetal` parameterization avoids loops/cusps at sharp corners and
    // hugs the points tighter than the default `catmullrom`.
    const curve = new THREE.CatmullRomCurve3(points, false, "centripetal", 0.5);

    // Resample by arc length for fast position lookup.
    const SAMPLES = 500;
    const raw: THREE.Vector3[] = [];
    for (let i = 0; i <= SAMPLES; i++) raw.push(curve.getPoint(i / SAMPLES));
    let len = 0;
    let prev = raw[0]!;
    this.samples.push({ s: 0, pos: prev.clone(), tan: this.tangentBetween(raw[0]!, raw[1]!) });
    for (let i = 1; i <= SAMPLES; i++) {
      const p = raw[i]!;
      len += p.distanceTo(prev);
      const next = raw[Math.min(SAMPLES, i + 1)]!;
      this.samples.push({ s: len, pos: p.clone(), tan: this.tangentBetween(prev, next) });
      prev = p;
    }
    this.length = len;
    this.platforms = this.buildPlatforms(targetLength, seed);
    // Trim arc length so the requested target lines up with the spline end —
    // we slightly overshoot above for the runway.
  }

  private buildPlatforms(targetLength: number, seed: number): PlatformStep[] {
    const rng = createRng(seed ^ 0x51f15e);
    const platforms: PlatformStep[] = [];
    const first = 115 + rng() * 25;
    const spacing = 170;
    for (let i = 0; i < 4; i++) {
      const start = first + i * spacing + (rng() - 0.5) * 36;
      if (start < 70 || start > targetLength - 95) continue;
      if (this.isNearCorner(start, 24) || this.isNearCorner(start + 55, 24)) continue;
      const ramp = 16 + rng() * 6;
      const top = 28 + rng() * 12;
      const drop = 5.5 + rng() * 3.5;
      const height = 1.6 + rng() * 1.6;
      platforms.push({
        start,
        rampUpEnd: start + ramp,
        topEnd: start + ramp + top,
        dropEnd: start + ramp + top + drop,
        height,
      });
    }
    return platforms;
  }

  private tangentBetween(a: THREE.Vector3, b: THREE.Vector3): THREE.Vector3 {
    const t = b.clone().sub(a);
    t.y = 0;
    return t.normalize();
  }

  private indexFor(s: number): number {
    // Clamp + binary search.
    s = Math.max(0, Math.min(this.length, s));
    let lo = 0;
    let hi = this.samples.length - 1;
    while (lo < hi - 1) {
      const mid = (lo + hi) >> 1;
      if (this.samples[mid]!.s <= s) lo = mid;
      else hi = mid;
    }
    return lo;
  }

  /** World position at arc length `s`, with optional lateral lane offset (m). */
  positionAt(s: number, laneOffset = 0, target?: THREE.Vector3): THREE.Vector3 {
    const i = this.indexFor(s);
    const a = this.samples[i]!;
    const b = this.samples[i + 1] ?? a;
    const t = b.s > a.s ? (s - a.s) / (b.s - a.s) : 0;
    const out = target ?? new THREE.Vector3();
    out.copy(a.pos).lerp(b.pos, t);
    out.y = this.heightAt(s);
    if (laneOffset !== 0) {
      const tan = this.tangentAt(s, _tmpTan);
      // Perpendicular chosen so that:
      //   1. positive offset visually appears on the player's LEFT (matches
      //      LANE_POSITIONS where higher index = more left), and
      //   2. mesh-local +X (after rotation.y = yaw) lands at the same world
      //      position positionAt would put a positive offset, so multi-lane
      //      obstacles render where their `lanes()` check expects them.
      out.x += tan.z * laneOffset;
      out.z += -tan.x * laneOffset;
    }
    return out;
  }

  heightAt(s: number): number {
    for (const p of this.platforms) {
      if (s < p.start || s > p.dropEnd) continue;
      if (s < p.rampUpEnd) {
        const t = (s - p.start) / Math.max(0.001, p.rampUpEnd - p.start);
        return p.height * smoothstep(t);
      }
      if (s < p.topEnd) return p.height;
      const t = (s - p.topEnd) / Math.max(0.001, p.dropEnd - p.topEnd);
      return p.height * (1 - smoothstep(t));
    }
    return 0;
  }

  heightSlopeAt(s: number, sampleDistance = 1.2): number {
    const a = this.heightAt(Math.max(0, s - sampleDistance));
    const b = this.heightAt(Math.min(this.length, s + sampleDistance));
    return (b - a) / (sampleDistance * 2);
  }

  tangentAt(s: number, target?: THREE.Vector3): THREE.Vector3 {
    const i = this.indexFor(s);
    const a = this.samples[i]!;
    const b = this.samples[i + 1] ?? a;
    const t = b.s > a.s ? (s - a.s) / (b.s - a.s) : 0;
    const out = target ?? new THREE.Vector3();
    out.copy(a.tan).lerp(b.tan, t).normalize();
    return out;
  }

  /** Y-axis rotation matching the tangent (use as `mesh.rotation.y`). */
  yawAt(s: number): number {
    const tan = this.tangentAt(s, _tmpTan);
    return Math.atan2(tan.x, tan.z);
  }
}

const _tmpTan = new THREE.Vector3();

function smoothstep(t: number): number {
  t = Math.max(0, Math.min(1, t));
  return t * t * (3 - 2 * t);
}
